import {
    Lesson,
    LessonListItem,
    MyLessonsResponse,
    PaginatedResponse,
    ScheduledClass,
    ScheduleNotificationsResponse,
} from "@types";
import api from "./api";

export interface LessonFilters {
  level?: string;
  status?: string;
  search?: string;
  page?: number;
}

export const lessonService = {
  async getLessons(
    filters: LessonFilters = {},
  ): Promise<PaginatedResponse<LessonListItem>> {
    const params = new URLSearchParams();

    if (filters.level) params.append("level", filters.level);
    if (filters.status) params.append("status", filters.status);
    if (filters.search) params.append("search", filters.search);
    if (filters.page) params.append("page", String(filters.page));

    const response = await api.get(`/lessons/?${params.toString()}`);
    return response.data;
  },

  async getLesson(id: string): Promise<Lesson> {
    const response = await api.get(`/lessons/${id}/`);
    return response.data;
  },

  async createLesson(data: Partial<Lesson>): Promise<Lesson> {
    const response = await api.post("/lessons/", data);
    return response.data;
  },

  async updateLesson(id: string, data: Partial<Lesson>): Promise<Lesson> {
    const response = await api.patch(`/lessons/${id}/`, data);
    return response.data;
  },

  async deleteLesson(id: string): Promise<void> {
    await api.delete(`/lessons/${id}/`);
  },

  // Get lessons for the current student's level
  // For admin/teacher, can pass level code to view a specific level
  async getMyLessons(levelCode?: string): Promise<MyLessonsResponse> {
    // Use axios params so codes with special chars (e.g. "A2+") are encoded
    // correctly — a raw "?level=A2+" would be read as "A2 " on the server.
    const response = await api.get("/lessons/my_lessons/", {
      params: levelCode ? { level: levelCode } : undefined,
    });
    return response.data;
  },

  // Scheduled Classes
  async getScheduledClasses(): Promise<ScheduledClass[]> {
    const response = await api.get("/lessons/scheduled/");
    return response.data;
  },

  async getUpcomingClasses(): Promise<ScheduledClass[]> {
    const response = await api.get("/lessons/scheduled/upcoming/");
    return response.data;
  },

  async getScheduledClass(id: string): Promise<ScheduledClass> {
    const response = await api.get(`/lessons/scheduled/${id}/`);
    return response.data;
  },

  async createScheduledClass(
    data: Partial<ScheduledClass>,
  ): Promise<ScheduledClass> {
    const response = await api.post("/lessons/scheduled/", data);
    return response.data;
  },

  async confirmClass(id: string): Promise<ScheduledClass> {
    const response = await api.post(`/lessons/scheduled/${id}/confirm/`);
    return response.data;
  },

  async startClass(id: string): Promise<ScheduledClass> {
    const response = await api.post(`/lessons/scheduled/${id}/start/`);
    return response.data;
  },

  async completeClass(id: string): Promise<ScheduledClass> {
    const response = await api.post(`/lessons/scheduled/${id}/complete/`);
    return response.data;
  },

  async cancelClass(id: string): Promise<ScheduledClass> {
    const response = await api.post(`/lessons/scheduled/${id}/cancel/`);
    return response.data;
  },

  // Schedule Notifications (for students)
  async getScheduleNotifications(): Promise<ScheduleNotificationsResponse> {
    const response = await api.get("/schedule/notifications/");
    return response.data;
  },

  async dismissScheduleNotification(notificationId: string): Promise<void> {
    await api.post("/schedule/notifications/", {
      notification_id: notificationId,
    });
  },
};
